const CAROUSEL_HINT_MODE = 'until-interaction'; // 'once' | 'until-interaction'
const CAROUSEL_HINT_VISIBILITY_THRESHOLD = 0.65;
const CAROUSEL_INTERACTION_DRAG_THRESHOLD = 28;
const CAROUSEL_INTERACTION_HORIZONTAL_RATIO = 1.5;
const CAROUSEL_INTERACTION_SCROLL_THRESHOLD = 8;
const CAROUSEL_SELECTOR = [
  '.resource-list__carousel > slideshow-component',
  '.nw-featured-colls__carousel > slideshow-component',
  '.nw-featured-pages__carousel > slideshow-component',
].join(',');
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;

function hasScrollableContent(slideshow) {
  const scroller = slideshow.querySelector('slideshow-slides');

  return scroller ? scroller.scrollWidth > scroller.clientWidth + 1 : false;
}

function shouldPlayHint(slideshow) {
  if (prefersReducedMotion()) return false;
  if (slideshow.dataset.carouselHintVisible !== 'true') return false;
  if (slideshow.hasAttribute('dragging') || slideshow.hasAttribute('transitioning')) return false;
  if (slideshow.dataset.carouselHint === 'playing') return false;
  if (slideshow.dataset.carouselHintInteracted === 'true') return false;
  if (CAROUSEL_HINT_MODE === 'once' && slideshow.dataset.carouselHintAnimated === 'true') return false;

  return hasScrollableContent(slideshow);
}

function playHint(slideshow) {
  if (!shouldPlayHint(slideshow)) return;

  slideshow.dataset.carouselHint = 'playing';
  slideshow.dataset.carouselHintAnimated = 'true';
}

function setupCarouselHint(slideshow) {
  if (slideshow.dataset.carouselHintReady === 'true') return;

  slideshow.dataset.carouselHintReady = 'true';
  const scroller = slideshow.querySelector('slideshow-slides');
  let lastScrollLeft = scroller?.scrollLeft ?? 0;
  let pointerStart = null;

  const markInteracted = () => {
    slideshow.dataset.carouselHintInteracted = 'true';
  };

  slideshow.addEventListener(
    'pointerdown',
    (event) => {
      pointerStart = {
        x: event.clientX,
        y: event.clientY,
      };
    },
    { passive: true }
  );
  slideshow.addEventListener(
    'pointermove',
    (event) => {
      if (!pointerStart || slideshow.dataset.carouselHintInteracted === 'true') return;

      const deltaX = Math.abs(event.clientX - pointerStart.x);
      const deltaY = Math.abs(event.clientY - pointerStart.y);

      if (
        deltaX >= CAROUSEL_INTERACTION_DRAG_THRESHOLD &&
        deltaX > deltaY * CAROUSEL_INTERACTION_HORIZONTAL_RATIO
      ) {
        markInteracted();
      }
    },
    { passive: true }
  );
  slideshow.addEventListener(
    'pointerup',
    () => {
      pointerStart = null;
    },
    { passive: true }
  );
  slideshow.addEventListener(
    'pointercancel',
    () => {
      pointerStart = null;
    },
    { passive: true }
  );
  slideshow.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('slideshow-arrows, slideshow-controls')) {
      markInteracted();
    }
  });
  scroller?.addEventListener(
    'scroll',
    () => {
      const deltaX = Math.abs(scroller.scrollLeft - lastScrollLeft);
      lastScrollLeft = scroller.scrollLeft;

      if (slideshow.dataset.carouselHint === 'playing') return;
      if (deltaX >= CAROUSEL_INTERACTION_SCROLL_THRESHOLD) {
        markInteracted();
      }
    },
    { passive: true }
  );

  slideshow.addEventListener('animationend', (event) => {
    if (event.animationName !== 'product-carousel-hint') return;

    delete slideshow.dataset.carouselHint;
  });

  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      slideshow.dataset.carouselHintVisible = entry?.isIntersecting ? 'true' : 'false';
      playHint(slideshow);
    },
    {
      threshold: CAROUSEL_HINT_VISIBILITY_THRESHOLD,
    }
  );

  visibilityObserver.observe(slideshow);
}

function setupCarouselHints(root = document) {
  root.querySelectorAll(CAROUSEL_SELECTOR).forEach(setupCarouselHint);
}

setupCarouselHints();

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches(CAROUSEL_SELECTOR)) {
        setupCarouselHint(node);
      } else {
        setupCarouselHints(node);
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
